// Rời form tạo đơn khi đã nhập khách mới (chưa có trong danh bạ) → hỏi có lưu khách vào Khách quen không,
// để lỡ thoát ra thì lần sau chọn lại ngay, khỏi nhập lại. Có → lưu (POST /customers) rồi rời trang;
// Không → rời trang luôn; đóng hộp / "Ở lại" → về form, thông tin vẫn còn nguyên.

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Loader2, MapPin, UserRound } from "lucide-react"
import { addressLine } from "@shoptrack/common/customer"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ApiError, post } from "@/lib/api"
import { phoneDisplay } from "@/lib/format"
import type { CustomerToSave } from "@/lib/orderLeave"

export function SaveCustomerOnLeaveDialog({
  open,
  customer,
  onLeave,
  onStay,
}: {
  open: boolean
  /** Khách sẽ lưu — giữ nguyên lúc hộp đang đóng dần */
  customer: CustomerToSave | null
  /** Đi tiếp tới nơi người bán vừa bấm */
  onLeave: () => void
  /** Ở lại form */
  onStay: () => void
}) {
  const qc = useQueryClient()
  const save = useMutation({
    mutationFn: (c: CustomerToSave) => post<{ customer: { id: string; name: string } }>("/customers", c),
    onSuccess: ({ customer: c }) => {
      void qc.invalidateQueries({ queryKey: ["customers"] })
      toast.success(`Đã lưu khách ${c.name} vào Khách quen`)
      onLeave()
    },
    onError: (e) => {
      // SĐT đã có trong danh bạ (vd. đã bấm "Bỏ qua" ở gợi ý khách quen) — khách đã được lưu từ trước
      if (e instanceof ApiError && e.code === "customer.phone_exists") {
        toast.info(`Số điện thoại này đã có trong danh bạ — khách ${(e.details as { name?: string })?.name ?? ""}`.trim())
        onLeave()
      }
    },
  })
  const failed = save.isError && !(save.error instanceof ApiError && save.error.code === "customer.phone_exists")

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o || save.isPending) return
        save.reset()
        onStay()
      }}
    >
      <DialogContent className="gap-4 sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader className="pr-10">
          <DialogTitle className="text-lg font-semibold">Lưu thông tin khách hàng?</DialogTitle>
        </DialogHeader>

        {customer && (
          <div className="space-y-1 rounded-lg bg-paperSoft px-3 py-2.5 text-sm">
            <p className="flex items-center gap-2 text-ink">
              <UserRound className="size-4 shrink-0 text-muted" />
              <b className="min-w-0 truncate">{customer.name}</b>
              <span className="shrink-0 tabular-nums text-inkSoft">{phoneDisplay(customer.phone)}</span>
            </p>
            {customer.address && (
              <p className="flex items-start gap-2 text-inkSoft">
                <MapPin className="mt-0.5 size-4 shrink-0 text-muted" />
                <span>{addressLine(customer.address)}</span>
              </p>
            )}
          </div>
        )}

        {failed && <p className="text-sm text-danger">{(save.error as Error)?.message || "Không lưu được khách"}</p>}

        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" className="h-12 text-base" disabled={save.isPending} onClick={() => (save.reset(), onLeave())}>
            Không
          </Button>
          <Button type="button" className="h-12 text-base font-semibold" disabled={save.isPending || !customer} onClick={() => customer && save.mutate(customer)}>
            {save.isPending && <Loader2 className="animate-spin" />}
            Có, lưu khách
          </Button>
        </div>
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => {
            save.reset()
            onStay()
          }}
          className="-mt-1 min-h-11 cursor-pointer text-sm text-muted hover:text-ink disabled:cursor-not-allowed desktop:min-h-0"
        >
          Ở lại tạo đơn tiếp
        </button>
      </DialogContent>
    </Dialog>
  )
}
